import { createModal } from "@stainless-code/react-modal-manager";
import { TriangleAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { LEMON_CUSTOMER_PORTAL_URL } from "@/config/lemon";
import { SubscriptionsStatusOptions } from "@/types/pocketbase.ts";

type Props = {
	status: SubscriptionsStatusOptions;
};

export const subscriptionBlockedModal = createModal<Props>(({ status, close }) => {
	const { t } = useTranslation();

	const getStatusMessage = () => {
		if (status === SubscriptionsStatusOptions.on_trial) {
			return {
				title: t(($) => $.modules.settings.subscription.trial_expired.title),
				description: t(($) => $.modules.settings.subscription.trial_expired.message),
			};
		}

		switch (status) {
			case SubscriptionsStatusOptions.expired:
				return {
					title: t(($) => $.modules.settings.subscription.expired.title),
					description: t(($) => $.modules.settings.subscription.expired.message),
				};
			case SubscriptionsStatusOptions.past_due:
				return {
					title: t(($) => $.modules.settings.subscription.past_due.title),
					description: t(($) => $.modules.settings.subscription.past_due.message),
				};
			case SubscriptionsStatusOptions.unpaid:
				return {
					title: t(($) => $.modules.settings.subscription.unpaid.title),
					description: t(($) => $.modules.settings.subscription.unpaid.message),
				};
			case SubscriptionsStatusOptions.cancelled:
				return {
					title: t(($) => $.modules.settings.subscription.cancelled.title),
					description: t(($) => $.modules.settings.subscription.cancelled.message),
				};
			case SubscriptionsStatusOptions.paused:
				return {
					title: t(($) => $.modules.settings.subscription.paused.title),
					description: t(($) => $.modules.settings.subscription.paused.message),
				};
			default:
				return {
					title: t(($) => $.modules.settings.subscription.expired.title),
					description: t(($) => $.modules.settings.subscription.expired.message),
				};
		}
	};

	const { title, description } = getStatusMessage();

	return (
		<AlertDialog onOpenChange={(open) => !open && close()} open={true}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle className="inline-flex items-center gap-2">
						<TriangleAlertIcon />
						{title}
					</AlertDialogTitle>
					<AlertDialogDescription>{description}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<Button onClick={close} variant="outline">
						{t(($) => $.common.actions.cancel)}
					</Button>
					<a href={LEMON_CUSTOMER_PORTAL_URL} rel="noopener noreferrer" target="_blank">
						<Button onClick={close}>{t(($) => $.modules.settings.subscription.button)}</Button>
					</a>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
});
